// src/pages/DocumentIngestionPage.jsx
//
// Manual PDF page selection + AI ingestion workflow.
//
// Step 1 → Upload raw PDF (drag-drop or click)
// Step 2 → Select pages (range text input + numbered grid)
// Step 3 → Set report metadata (company, year, type)
// Step 4 → Start ingestion → poll progress
//
import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Divider,
    FormControl,
    InputLabel,
    LinearProgress,
    MenuItem,
    Select,
    Step,
    StepLabel,
    Stepper,
    TextField,
    Typography,
    Alert,
    Tooltip,
} from '@mui/material';
import {
    CloudUpload,
    CheckCircle,
    SelectAll,
    ClearAll,
    PlayArrow,
    Refresh,
    InsertDriveFile,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { previewPdf, ingestSelectedPages, getJobStatus } from '../services/documentIngestionService';

// ── constants ─────────────────────────────────────────────────────────────────

const STEPS = ['Upload PDF', 'Select Pages', 'Report Details', 'Start Ingestion'];

const DOCUMENT_TYPES = [
    { value: 'annual_report', label: 'Annual Report' },
    { value: 'concall_transcript', label: 'Concall Transcript' },
    { value: 'investor_presentation', label: 'Investor Presentation' },
    { value: 'quarterly_report', label: 'Quarterly Report' },
];

const POLL_INTERVAL_MS = 3000;

// ── helpers ───────────────────────────────────────────────────────────────────

/**
 * Parse a range string like "40-69, 106-237" or "1,2,5" into a sorted
 * deduplicated Set of 1-based page numbers, clamped to [1, maxPage].
 */
function parseRangeString(raw, maxPage) {
    const pages = new Set();
    const parts = raw.split(/[,;]+/);
    for (const part of parts) {
        const trimmed = part.trim();
        if (!trimmed) continue;
        const rangeMatch = trimmed.match(/^(\d+)\s*[-–]\s*(\d+)$/);
        if (rangeMatch) {
            let [, a, b] = rangeMatch.map(Number);
            if (a > b) [a, b] = [b, a];
            for (let i = a; i <= b; i++) {
                if (i >= 1 && i <= maxPage) pages.add(i);
            }
        } else if (/^\d+$/.test(trimmed)) {
            const n = Number(trimmed);
            if (n >= 1 && n <= maxPage) pages.add(n);
        } else {
            return null; // parse error
        }
    }
    return pages;
}

function statusColor(status) {
    const map = {
        queued: 'info',
        processing: 'warning',
        completed: 'success',
        failed: 'error',
    };
    return map[status] || 'default';
}

// ── sub-components ────────────────────────────────────────────────────────────

function UploadZone({ onFileLoaded }) {
    const [dragging, setDragging] = useState(false);
    const [loading, setLoading] = useState(false);
    const inputRef = useRef();

    const handleFile = useCallback(
        async (file) => {
            if (!file || file.type !== 'application/pdf') {
                toast.error('Please select a PDF file.');
                return;
            }
            setLoading(true);
            try {
                const info = await previewPdf(file);
                onFileLoaded(file, info);
            } catch (err) {
                toast.error(`Could not read PDF: ${err.message}`);
            } finally {
                setLoading(false);
            }
        },
        [onFileLoaded]
    );

    const onDrop = useCallback(
        (e) => {
            e.preventDefault();
            setDragging(false);
            const file = e.dataTransfer.files?.[0];
            if (file) handleFile(file);
        },
        [handleFile]
    );

    return (
        <Box
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={onDrop}
            onClick={() => inputRef.current?.click()}
            sx={{
                border: `2px dashed ${dragging ? '#1976d2' : '#555'}`,
                borderRadius: 2,
                p: 6,
                textAlign: 'center',
                cursor: 'pointer',
                bgcolor: dragging ? 'action.hover' : 'background.paper',
                transition: 'all 0.2s',
                '&:hover': { bgcolor: 'action.hover', borderColor: '#1976d2' },
            }}
        >
            <input
                ref={inputRef}
                type="file"
                accept="application/pdf"
                style={{ display: 'none' }}
                onChange={(e) => handleFile(e.target.files?.[0])}
            />
            {loading ? (
                <CircularProgress size={40} />
            ) : (
                <>
                    <CloudUpload sx={{ fontSize: 56, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" gutterBottom>
                        Drop Annual Report PDF here
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        or click to browse — PDF only, up to 150 MB
                    </Typography>
                </>
            )}
        </Box>
    );
}

function PageGrid({ totalPages, selected, onToggle }) {
    // Render pages in rows of 20
    const rows = [];
    for (let i = 1; i <= totalPages; i += 20) {
        rows.push({ start: i, end: Math.min(i + 19, totalPages) });
    }

    return (
        <Box sx={{ fontFamily: 'monospace' }}>
            {rows.map(({ start, end }) => (
                <Box key={start} sx={{ display: 'flex', flexWrap: 'wrap', gap: '4px', mb: '4px' }}>
                    {Array.from({ length: end - start + 1 }, (_, k) => {
                        const page = start + k;
                        const isSelected = selected.has(page);
                        return (
                            <Tooltip key={page} title={`Page ${page}`} placement="top" arrow>
                                <Box
                                    onClick={() => onToggle(page)}
                                    sx={{
                                        width: 36,
                                        height: 28,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        borderRadius: 1,
                                        cursor: 'pointer',
                                        fontSize: '0.68rem',
                                        fontWeight: isSelected ? 700 : 400,
                                        bgcolor: isSelected ? 'primary.main' : 'background.paper',
                                        color: isSelected ? 'primary.contrastText' : 'text.secondary',
                                        border: '1px solid',
                                        borderColor: isSelected ? 'primary.main' : 'divider',
                                        userSelect: 'none',
                                        '&:hover': {
                                            bgcolor: isSelected ? 'primary.dark' : 'action.hover',
                                        },
                                        transition: 'all 0.1s',
                                    }}
                                >
                                    {page}
                                </Box>
                            </Tooltip>
                        );
                    })}
                </Box>
            ))}
        </Box>
    );
}

function JobStatusCard({ jobId, companyId, onDone }) {
    const [job, setJob] = useState(null);
    const [pollError, setPollError] = useState(null);  // transient, not fatal
    const [consecutiveErrors, setConsecutiveErrors] = useState(0);
    const timerRef = useRef(null);

    useEffect(() => {
        let cancelled = false;
        const poll = async () => {
            try {
                const j = await getJobStatus(companyId, jobId);
                if (cancelled) return;
                setConsecutiveErrors(0);
                setPollError(null);
                if (j) {
                    setJob(j);
                    if (j.status === 'completed' || j.status === 'failed') {
                        clearInterval(timerRef.current);
                        if (j.status === 'completed') onDone?.();
                    }
                }
            } catch (err) {
                if (cancelled) return;
                setPollError(err.message);
                setConsecutiveErrors((n) => n + 1);
            }
        };
        poll();
        timerRef.current = setInterval(poll, POLL_INTERVAL_MS);
        return () => {
            cancelled = true;
            clearInterval(timerRef.current);
        };
    }, [jobId, companyId, onDone]);

    // Only treat as fatal after 5 consecutive failures with no job data
    if (consecutiveErrors >= 5 && !job)
        return <Alert severity="error">Could not reach server after several retries. Check that the Azure Functions host is running.</Alert>;
    if (!job) return <LinearProgress />;

    const isDone = job.status === 'completed' || job.status === 'failed';
    const progress = job.status === 'processing' && job.chunks_total > 0
        ? Math.round((job.chunks_created || 0) / job.chunks_total * 100)
        : undefined;

    return (
        <Card variant="outlined" sx={{ mt: 2 }}>
            <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Chip
                        label={job.status?.toUpperCase()}
                        color={statusColor(job.status)}
                        size="small"
                    />
                    {!isDone && <CircularProgress size={16} />}
                    {job.status === 'completed' && <CheckCircle color="success" />}
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {job.message || 'Working…'}
                </Typography>

                {progress !== undefined && (
                    <Box sx={{ mb: 1 }}>
                        <LinearProgress variant="determinate" value={progress} />
                        <Typography variant="caption" color="text.secondary">
                            {job.chunks_created || 0} / {job.chunks_total} chunks
                        </Typography>
                    </Box>
                )}

                {!isDone && (
                    <Typography variant="caption" color="text.secondary">
                        Auto-refreshing every {POLL_INTERVAL_MS / 1000}s…
                        {pollError && (
                            <span style={{ color: '#f57c00' }}> (retrying — {pollError})</span>
                        )}
                    </Typography>
                )}

                {job.status === 'completed' && (
                    <Alert severity="success" sx={{ mt: 1 }}>
                        Ingestion complete — {job.chunks_created || job.chunk_count || '?'} chunks created and indexed.
                    </Alert>
                )}
                {job.status === 'failed' && (
                    <Alert severity="error" sx={{ mt: 1 }}>
                        {job.error || job.message || 'Processing failed.'}
                    </Alert>
                )}

                <Typography variant="caption" display="block" sx={{ mt: 1, opacity: 0.5 }}>
                    Job ID: {jobId}
                </Typography>
            </CardContent>
        </Card>
    );
}

// ── main page ─────────────────────────────────────────────────────────────────

export default function DocumentIngestionPage() {
    const navigate = useNavigate();
    const [activeStep, setActiveStep] = useState(0);

    // Step 1 state
    const [pdfFile, setPdfFile] = useState(null);
    const [pdfInfo, setPdfInfo] = useState(null); // {page_count, file_size_kb, filename}

    // Step 2 state
    const [selectedPages, setSelectedPages] = useState(new Set());
    const [rangeInput, setRangeInput] = useState('');
    const [rangeError, setRangeError] = useState('');

    // Step 3 state
    const [metadata, setMetadata] = useState({
        companyId: '',
        companyName: '',
        fiscalYear: new Date().getFullYear(),
        documentType: 'annual_report',
        basis: 'consolidated',
    });

    // Step 4 state
    const [jobId, setJobId] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(null);

    // ── step 1: file loaded ───────────────────────────────────────────────────
    const handleFileLoaded = useCallback((file, info) => {
        setPdfFile(file);
        setPdfInfo(info);
        setSelectedPages(new Set());
        setRangeInput('');
        setRangeError('');
        setActiveStep(1);
        toast.success(`Loaded ${info.filename} — ${info.page_count} pages`);
    }, []);

    // ── step 2: page selection helpers ───────────────────────────────────────
    const togglePage = useCallback((page) => {
        setSelectedPages((prev) => {
            const next = new Set(prev);
            next.has(page) ? next.delete(page) : next.add(page);
            return next;
        });
    }, []);

    const applyRange = useCallback(() => {
        if (!rangeInput.trim()) return;
        const pages = parseRangeString(rangeInput, pdfInfo.page_count);
        if (!pages) {
            setRangeError('Invalid range. Use formats like "40-69, 106-237" or "1,5,10".');
            return;
        }
        setRangeError('');
        setSelectedPages(pages);
        toast.info(`Selected ${pages.size} pages`);
    }, [rangeInput, pdfInfo]);

    const handleRangeKeyDown = (e) => {
        if (e.key === 'Enter') applyRange();
    };

    const selectAll = () => {
        const all = new Set(Array.from({ length: pdfInfo.page_count }, (_, i) => i + 1));
        setSelectedPages(all);
    };

    const clearAll = () => setSelectedPages(new Set());

    // ── step 3 → 4: submit ───────────────────────────────────────────────────
    const canSubmit =
        pdfFile &&
        selectedPages.size > 0 &&
        metadata.companyId.trim() &&
        metadata.companyName.trim() &&
        metadata.fiscalYear;

    const handleSubmit = async () => {
        if (!canSubmit) return;
        setSubmitting(true);
        setSubmitError(null);
        try {
            const result = await ingestSelectedPages(
                pdfFile,
                Array.from(selectedPages).sort((a, b) => a - b),
                metadata
            );
            setJobId(result.job_id);
            setActiveStep(3);
            toast.success(`Ingestion started — ${result.selected_page_count} pages queued`);
        } catch (err) {
            setSubmitError(err.message);
            toast.error(`Ingestion failed: ${err.message}`);
        } finally {
            setSubmitting(false);
        }
    };

    const resetAll = () => {
        setPdfFile(null);
        setPdfInfo(null);
        setSelectedPages(new Set());
        setRangeInput('');
        setRangeError('');
        setJobId(null);
        setSubmitError(null);
        setActiveStep(0);
    };

    // ── render ────────────────────────────────────────────────────────────────
    return (
        <Box sx={{ maxWidth: 960, mx: 'auto', p: 3 }}>
            <Button
                variant="text"
                onClick={() => navigate('/home')}
                sx={{ mb: 1, textTransform: 'none', fontWeight: 600 }}
            >
                ← Home
            </Button>
            <Typography variant="h4" fontWeight={700} gutterBottom>
                Ingest Annual Report
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Upload a raw PDF, select only the meaningful pages, then run the AI pipeline on the cleaned version.
            </Typography>

            <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
                {STEPS.map((label) => (
                    <Step key={label}>
                        <StepLabel>{label}</StepLabel>
                    </Step>
                ))}
            </Stepper>

            {/* ── Step 0: Upload ── */}
            {activeStep === 0 && (
                <UploadZone onFileLoaded={handleFileLoaded} />
            )}

            {/* ── Step 1: Select Pages ── */}
            {activeStep === 1 && pdfInfo && (
                <Card variant="outlined">
                    <CardContent>
                        {/* File info bar */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <InsertDriveFile color="primary" />
                            <Typography variant="subtitle1" fontWeight={600}>
                                {pdfInfo.filename}
                            </Typography>
                            <Chip label={`${pdfInfo.page_count} pages`} size="small" />
                            <Chip label={`${pdfInfo.file_size_kb.toLocaleString()} KB`} size="small" variant="outlined" />
                            <Box sx={{ flex: 1 }} />
                            <Button size="small" onClick={resetAll} startIcon={<Refresh />}>
                                Change PDF
                            </Button>
                        </Box>

                        <Divider sx={{ mb: 2 }} />

                        {/* PDF vs printed page number warning */}
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            <strong>Use PDF page numbers, not printed numbers.</strong>
                            {' '}Your PDF viewer shows the real position (e.g. "24 of 133"). The numbers printed
                            inside the report (e.g. "40 | Annual Report") are different — cover pages and TOC
                            shift them. Always enter the numbers shown in your PDF viewer's navigation bar.
                        </Alert>

                        {/* Range input */}
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 2, flexWrap: 'wrap' }}>
                            <TextField
                                label='PDF page ranges (e.g. "24-53, 90-221")'
                                value={rangeInput}
                                onChange={(e) => { setRangeInput(e.target.value); setRangeError(''); }}
                                onKeyDown={handleRangeKeyDown}
                                error={!!rangeError}
                                helperText={rangeError || 'Use PDF viewer page numbers, not printed report page numbers'}
                                size="small"
                                sx={{ minWidth: 340 }}
                            />
                            <Button variant="contained" onClick={applyRange} sx={{ mt: '4px' }}>
                                Apply
                            </Button>
                            <Button variant="outlined" onClick={selectAll} startIcon={<SelectAll />} sx={{ mt: '4px' }}>
                                All
                            </Button>
                            <Button variant="outlined" onClick={clearAll} startIcon={<ClearAll />} sx={{ mt: '4px' }}>
                                Clear
                            </Button>
                        </Box>

                        {/* Selection summary */}
                        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Chip
                                label={`${selectedPages.size} of ${pdfInfo.page_count} pages selected`}
                                color={selectedPages.size > 0 ? 'primary' : 'default'}
                                variant={selectedPages.size > 0 ? 'filled' : 'outlined'}
                            />
                            {selectedPages.size > 0 && (
                                <Typography variant="caption" color="text.secondary">
                                    Click individual pages below to toggle
                                </Typography>
                            )}
                        </Box>

                        {/* Page grid */}
                        <Box sx={{ maxHeight: 420, overflowY: 'auto', pr: 1 }}>
                            <PageGrid
                                totalPages={pdfInfo.page_count}
                                selected={selectedPages}
                                onToggle={togglePage}
                            />
                        </Box>

                        <Divider sx={{ mt: 2, mb: 2 }} />

                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                            <Button onClick={() => setActiveStep(0)}>Back</Button>
                            <Button
                                variant="contained"
                                disabled={selectedPages.size === 0}
                                onClick={() => setActiveStep(2)}
                            >
                                Continue →
                            </Button>
                        </Box>
                    </CardContent>
                </Card>
            )}

            {/* ── Step 2: Metadata ── */}
            {activeStep === 2 && (
                <Card variant="outlined">
                    <CardContent>
                        <Typography variant="h6" fontWeight={600} gutterBottom>
                            Report Metadata
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            {selectedPages.size} pages selected from {pdfInfo?.filename}
                        </Typography>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 480 }}>
                            <TextField
                                label="Company Name"
                                value={metadata.companyName}
                                onChange={(e) => setMetadata((m) => ({ ...m, companyName: e.target.value }))}
                                required
                                autoFocus
                            />
                            <TextField
                                label="Company ID"
                                value={metadata.companyId}
                                onChange={(e) => setMetadata((m) => ({ ...m, companyId: e.target.value.toUpperCase() }))}
                                required
                                helperText="Short uppercase identifier, e.g. RATNAMANI"
                            />
                            <TextField
                                label="Fiscal Year"
                                type="number"
                                value={metadata.fiscalYear}
                                onChange={(e) => setMetadata((m) => ({ ...m, fiscalYear: parseInt(e.target.value, 10) }))}
                                required
                                inputProps={{ min: 2000, max: 2100 }}
                            />
                            <FormControl>
                                <InputLabel>Document Type</InputLabel>
                                <Select
                                    value={metadata.documentType}
                                    label="Document Type"
                                    onChange={(e) => setMetadata((m) => ({ ...m, documentType: e.target.value }))}
                                >
                                    {DOCUMENT_TYPES.map((t) => (
                                        <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl>
                                <InputLabel>Basis</InputLabel>
                                <Select
                                    value={metadata.basis}
                                    label="Basis"
                                    onChange={(e) => setMetadata((m) => ({ ...m, basis: e.target.value }))}
                                >
                                    <MenuItem value="consolidated">Consolidated</MenuItem>
                                    <MenuItem value="standalone">Standalone</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>

                        {submitError && (
                            <Alert severity="error" sx={{ mt: 2 }}>{submitError}</Alert>
                        )}

                        <Divider sx={{ mt: 3, mb: 2 }} />

                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                            <Button onClick={() => setActiveStep(1)}>Back</Button>
                            <Button
                                variant="contained"
                                color="primary"
                                disabled={!canSubmit || submitting}
                                startIcon={submitting ? <CircularProgress size={16} /> : <PlayArrow />}
                                onClick={handleSubmit}
                            >
                                {submitting ? 'Starting…' : `Start Ingestion (${selectedPages.size} pages)`}
                            </Button>
                        </Box>
                    </CardContent>
                </Card>
            )}

            {/* ── Step 3: Status ── */}
            {activeStep === 3 && jobId && (
                <Card variant="outlined">
                    <CardContent>
                        <Typography variant="h6" fontWeight={600} gutterBottom>
                            Ingestion Running
                        </Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                            {metadata.companyName} • FY{metadata.fiscalYear} • {selectedPages.size} pages
                        </Typography>

                        <JobStatusCard
                            jobId={jobId}
                            companyId={metadata.companyId}
                            onDone={() => toast.success('Ingestion complete! Chunks are now indexed.')}
                        />

                        <Box sx={{ mt: 3, display: 'flex', gap: 1 }}>
                            <Button variant="outlined" startIcon={<Refresh />} onClick={resetAll}>
                                Ingest Another Report
                            </Button>
                        </Box>
                    </CardContent>
                </Card>
            )}
        </Box>
    );
}
